import { Injectable, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { SessionStorageService } from '@upr-web-primer/storage';
import { UrlInfoService } from '@upr-web-primer/url-info';

/**
 * SignupIdentityVerificationGuard
 *
 * Protects the /signup/identity-verification (OTP) route so users cannot
 * land on it via direct URL entry, refresh, or bookmark without having
 * just completed the Create Account form.
 *
 * Contract:
 *  - This route is a STEP inside the Create Account flow. The only
 *    legitimate way to reach it is by CreateAccountComponent submitting
 *    the form successfully, which writes handoff values into
 *    sessionStorage (EMAIL_USER_ID + LOGGEDIN_GUEST_ID) and then
 *    navigates here.
 *  - If either handoff value is missing / blank / unreadable, we
 *    return a UrlTree redirecting to Create Account. Returning a
 *    UrlTree (as opposed to `false` + router.navigate) makes the
 *    redirect atomic and race-free with Angular's navigation pipeline.
 *  - The CREATEACCOUNT_OTP feature flag is intentionally NOT consulted:
 *    the route only exists to support the OTP step, so if the flag is
 *    off, CreateAccountComponent will not navigate here to begin with.
 *    Making the guard flag-independent removes a race with the flag
 *    service's readiness and keeps `canActivate` fully synchronous.
 *  - The guard is fully synchronous (returns boolean | UrlTree), pure,
 *    and side-effect free.
 *  - Storage reads are wrapped defensively: some browser modes (private
 *    mode, sandboxed iframes) can throw on sessionStorage access. A
 *    throw is treated as "no session" and redirects.
 */

/** Session storage keys used for the Create Account → OTP handoff. */
const SESSION_KEYS = {
  EMAIL_USER_ID: 'EMAIL_USER_ID',
  LOGGEDIN_GUEST_ID: 'LOGGEDIN_GUEST_ID',
} as const;

/** Minimal shape of the AppRoutes injection token consumed here. */
interface AppRoutesToken {
  readonly CREATE_ACCOUNT: string;
}

@Injectable({
  providedIn: 'root',
})
export class SignupIdentityVerificationGuard {
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly router = inject(Router);
  private readonly urlInfoService = inject(UrlInfoService);
  private readonly appRoutes = inject<AppRoutesToken>('AppRoutes' as any);

  /**
   * Synchronous CanActivate implementation.
   *
   * Returns `true` when a valid signup session handoff exists in
   * sessionStorage, otherwise returns a UrlTree redirecting to the
   * localized Create Account route.
   */
  canActivate(): boolean | UrlTree {
    return this.hasValidSignupSession()
      ? true
      : this.buildCreateAccountUrlTree();
  }

  /**
   * True when BOTH handoff values are present and non-whitespace.
   * Defensive against storage access throws (private mode, sandboxed
   * iframes) — a throw is treated as "no session".
   */
  private hasValidSignupSession(): boolean {
    const email = this.readSessionValue(SESSION_KEYS.EMAIL_USER_ID);
    const guestId = this.readSessionValue(SESSION_KEYS.LOGGEDIN_GUEST_ID);
    return email.length > 0 && guestId.length > 0;
  }

  /**
   * Safely read a session storage value. Returns '' on any error or
   * missing/blank value.
   */
  private readSessionValue(key: string): string {
    try {
      const raw = this.sessionStorage.getItem(key);
      return typeof raw === 'string' ? raw.trim() : '';
    } catch {
      return '';
    }
  }

  /**
   * Build a UrlTree pointing at the localized /create-account route,
   * respecting the current :language/:country prefix (e.g. /en/us/).
   */
  private buildCreateAccountUrlTree(): UrlTree {
    const prefix = this.getUrlPrefix();
    const raw = `${prefix}/${this.appRoutes.CREATE_ACCOUNT}`;
    // Collapse duplicate slashes and strip any trailing slash so
    // parseUrl always sees a canonical path. Uses linear-time string
    // scans instead of regex to avoid super-linear regex runtime
    // (sonar rule typescript:S5852 — ReDoS hardening).
    const target = stripTrailingSlashes(collapseSlashes(raw)) || '/';
    return this.router.parseUrl(target);
  }

  /**
   * Normalise the current locale prefix so it always begins with '/'
   * and never ends with '/'. Falls back to '' if UrlInfoService is
   * not yet initialised.
   */
  private getUrlPrefix(): string {
    const raw = (this.urlInfoService?.getUrlPrefix?.() ?? '').trim();
    if (!raw) {
      return '';
    }
    const withLeading = raw.startsWith('/') ? raw : `/${raw}`;
    // Linear-time trailing-slash trim (ReDoS-safe alternative to
    // /\/+$/ — sonar rule typescript:S5852).
    return stripTrailingSlashes(withLeading);
  }
}

/** Single forward-slash character constant — avoids magic numbers/charCodeAt. */
const SLASH = '/';

/**
 * Collapse any run of consecutive '/' characters down to a single '/'.
 *
 * Linear O(n) scan — a ReDoS-safe alternative to `str.replace(/\/{2,}/g, '/')`
 * that sidesteps the Sonar `typescript:S5852` rule for regexes vulnerable
 * to super-linear runtime.
 */
function collapseSlashes(value: string): string {
  if (!value) {
    return '';
  }
  let result = '';
  let prevWasSlash = false;
  for (const ch of value) {
    const isSlash = ch === SLASH;
    if (isSlash && prevWasSlash) {
      continue;
    }
    result += ch;
    prevWasSlash = isSlash;
  }
  return result;
}

/**
 * Strip any trailing '/' characters from the end of the string.
 *
 * Linear O(n) walk from the end of the string — a ReDoS-safe alternative
 * to `str.replace(/\/+$/, '')` that avoids Sonar rule `typescript:S5852`.
 */
function stripTrailingSlashes(value: string): string {
  if (!value) {
    return '';
  }
  let end = value.length;
  while (end > 0 && value[end - 1] === SLASH) {
    end--;
  }
  return end === value.length ? value : value.slice(0, end);
}
